import React, { useState, useEffect } from 'react';
import { leadership } from '../services/api.js';
import { getRiskBadgeClass } from '../utils/riskUtils';
import { AuditTrail } from './AuditTrail.jsx';
import { CapacityPartners } from './CapacityPartners.jsx';
import './LeadershipDashboard.css';

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Color scale for the heat map cells
function getHeatColor(value, max) {
  if (!value || !max) return 'var(--color-bg)';
  const ratio = value / max;
  if (ratio >= 0.8) return 'var(--color-risk-red-bg)';
  if (ratio >= 0.6) return 'var(--color-risk-orange-bg)';
  if (ratio >= 0.3) return 'var(--color-risk-yellow-bg)';
  return 'var(--color-risk-green-bg)';
}

function getHeatTextColor(value, max) {
  if (!value || !max) return 'var(--color-text-muted)';
  const ratio = value / max;
  if (ratio >= 0.8) return 'var(--color-risk-red)';
  if (ratio >= 0.6) return 'var(--color-risk-orange)';
  if (ratio >= 0.3) return 'var(--color-risk-yellow)';
  return 'var(--color-risk-green)';
}

const TIER_COLORS = {
  green: 'var(--color-risk-green)',
  orange: 'var(--color-risk-orange)',
  red: 'var(--color-risk-red)',
  unscored: 'var(--color-text-muted)',
};

export function LeadershipDashboard() {
  const [hubTab, setHubTab] = useState('kpis');
  const [dashboard, setDashboard] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [accumulation, setAccumulation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadAll() {
      setLoading(true);
      setError(null);
      try {
        const [dashRes, overRes, accRes] = await Promise.allSettled([
          leadership.getDashboard(),
          leadership.getOverrides(),
          leadership.getAccumulation(),
        ]);

        if (dashRes.status === 'fulfilled') {
          const d = dashRes.value.data || dashRes.value;
          setDashboard(d);
        }
        if (overRes.status === 'fulfilled') {
          const o = overRes.value.data || overRes.value;
          setOverrides(Array.isArray(o) ? o : []);
        }
        if (accRes.status === 'fulfilled') {
          const a = accRes.value.data || accRes.value;
          setAccumulation(a);
        }

        // If the main dashboard call failed, show error
        if (dashRes.status === 'rejected') {
          setError(dashRes.reason?.message || 'Failed to load dashboard');
        }
      } catch (err) {
        setError(err.message || 'Failed to load leadership dashboard');
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, []);

  if (loading) {
    return (
      <div className="ld-loading">
        <div className="spinner" />
        <p>Loading leadership dashboard...</p>
      </div>
    );
  }

  if (error && !dashboard) {
    return (
      <div className="ld-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn btn--primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  const stats = dashboard || {};
  const statusBreakdown = stats.statusBreakdown || {};
  const tierBreakdown = stats.riskTierBreakdown || {};
  const capacity = stats.capacity || {};
  const quoteMetrics = stats.quoteMetrics || {};

  const totalSubs = stats.totalSubmissions || 0;
  const boundCount = statusBreakdown.bound || 0;
  const declinedCount = statusBreakdown.declined || 0;
  const inReviewCount = statusBreakdown.in_review || 0;
  const unassignedCount = stats.unassignedCount || 0;
  const avgRiskScore = stats.averageRiskScore;
  const utilizationPct = capacity.utilizationPct;
  const activeUWs = capacity.activeUnderwriters || 0;
  const totalPremium = quoteMetrics.totalPremium;
  const totalQuotes = quoteMetrics.totalQuotes || 0;
  const deliveredQuotes = quoteMetrics.deliveredQuotes || 0;

  const portfolio = Array.isArray(stats.portfolio) ? stats.portfolio : [];
  const accumulationData = accumulation || {};

  return (
    <div className="ld">
      {/* Hub Tab Bar */}
      <div className="hub-tabs">
        <button className={`hub-tab ${hubTab === 'kpis' ? 'hub-tab--active' : ''}`} onClick={() => setHubTab('kpis')}>DEX KPIs</button>
        <button className={`hub-tab ${hubTab === 'audit' ? 'hub-tab--active' : ''}`} onClick={() => setHubTab('audit')}>Audit Trail</button>
        <button className={`hub-tab ${hubTab === 'capacity' ? 'hub-tab--active' : ''}`} onClick={() => setHubTab('capacity')}>Capacity Partners</button>
      </div>

      {hubTab === 'audit' && <AuditTrail />}
      {hubTab === 'capacity' && <CapacityPartners />}
      {hubTab === 'kpis' && (
      <>
      <h1 className="ld__title">DEX KPIs</h1>

      {/* Stat cards - Row 1 */}
      <div className="ld__stats">
        <div className="ld__stat-card card">
          <span className="ld__stat-label">Total Submissions</span>
          <span className="ld__stat-value">{totalSubs}</span>
        </div>
        <div className="ld__stat-card card">
          <span className="ld__stat-label">Bound</span>
          <span className="ld__stat-value ld__stat-value--green">{boundCount}</span>
        </div>
        <div className="ld__stat-card card">
          <span className="ld__stat-label">Declined</span>
          <span className="ld__stat-value ld__stat-value--red">{declinedCount}</span>
        </div>
        <div className="ld__stat-card card">
          <span className="ld__stat-label">In Review</span>
          <span className="ld__stat-value ld__stat-value--yellow">{inReviewCount}</span>
        </div>
      </div>

      {/* Stat cards - Row 2 */}
      <div className="ld__stats">
        <div className="ld__stat-card card">
          <span className="ld__stat-label">Unassigned</span>
          <span className="ld__stat-value">{unassignedCount}</span>
        </div>
        <div className="ld__stat-card card">
          <span className="ld__stat-label">Avg Risk Score</span>
          <span className="ld__stat-value">
            {avgRiskScore != null ? avgRiskScore.toFixed(2) : '—'}
          </span>
        </div>
        <div className="ld__stat-card card">
          <span className="ld__stat-label">Total Premium</span>
          <span className="ld__stat-value">{formatCurrency(totalPremium)}</span>
        </div>
        <div className="ld__stat-card card">
          <span className="ld__stat-label">Capacity Utilization</span>
          <span className="ld__stat-value">
            {utilizationPct != null ? `${utilizationPct.toFixed(1)}%` : '—'}
          </span>
        </div>
      </div>

      {/* Risk Tier Breakdown */}
      {Object.keys(tierBreakdown).length > 0 && (
        <div className="ld__section">
          <h2 className="ld__section-title">Risk Tier Breakdown</h2>
          <div className="ld__stats">
            {Object.entries(tierBreakdown).map(([tier, count]) => (
              <div key={tier} className="ld__stat-card card">
                <span className="ld__stat-label" style={{ textTransform: 'capitalize' }}>
                  {tier}
                </span>
                <span
                  className="ld__stat-value"
                  style={{ color: TIER_COLORS[tier] || 'inherit' }}
                >
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Capacity & Quote Details */}
      <div className="ld__section">
        <h2 className="ld__section-title">Capacity & Quotes</h2>
        <div className="ld__table-wrap card">
          <table className="ld__table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Active Underwriters</td>
                <td>{activeUWs}</td>
              </tr>
              <tr>
                <td>Current Workload</td>
                <td>{capacity.currentWorkload ?? '—'}</td>
              </tr>
              <tr>
                <td>Total Capacity</td>
                <td>{capacity.totalCapacity ?? '—'}</td>
              </tr>
              <tr>
                <td>Utilization</td>
                <td>{utilizationPct != null ? `${utilizationPct.toFixed(1)}%` : '—'}</td>
              </tr>
              <tr>
                <td>Total Quotes</td>
                <td>{totalQuotes}</td>
              </tr>
              <tr>
                <td>Delivered Quotes</td>
                <td>{deliveredQuotes}</td>
              </tr>
              <tr>
                <td>Total Premium</td>
                <td>{formatCurrency(totalPremium)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Status Breakdown Detail */}
      {Object.keys(statusBreakdown).length > 0 && (
        <div className="ld__section">
          <h2 className="ld__section-title">Status Breakdown</h2>
          <div className="ld__table-wrap card">
            <table className="ld__table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(statusBreakdown).map(([status, count]) => (
                  <tr key={status}>
                    <td style={{ textTransform: 'capitalize' }}>
                      {status.replace(/_/g, ' ')}
                    </td>
                    <td>{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Portfolio Table */}
      <div className="ld__section">
        <h2 className="ld__section-title">Portfolio</h2>
        <div className="ld__table-wrap card">
          <table className="ld__table">
            <thead>
              <tr>
                <th>Insured</th>
                <th>Product</th>
                <th>Broker</th>
                <th>Risk Score</th>
                <th>Status</th>
                <th>Underwriter</th>
                <th>Premium</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {portfolio.length > 0 ? (
                portfolio.map((item, idx) => (
                  <tr key={item.SubmissionID || idx}>
                    <td className="ld__table-insured">{item.InsuredName || '—'}</td>
                    <td>{item.ProductName || '—'}</td>
                    <td>{item.BrokerName || '—'}</td>
                    <td>
                      <span className={getRiskBadgeClass(item.RiskScore)}>
                        {item.RiskScore != null && item.RiskScore >= 0 ? item.RiskScore : '—'}
                      </span>
                    </td>
                    <td>
                      <span className="ld__status" data-status={item.Status}>
                        {(item.Status || '—').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>{item.AssignedUWName || '—'}</td>
                    <td>{item.Premium ? formatCurrency(item.Premium) : '—'}</td>
                    <td className="ld__table-date">{formatDate(item.CreatedAt)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="ld__table-empty">No portfolio data available.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Overrides */}
      <div className="ld__section">
        <h2 className="ld__section-title">Overrides</h2>
        <div className="ld__table-wrap card">
          {Array.isArray(overrides) && overrides.length > 0 ? (
            <table className="ld__table">
              <thead>
                <tr>
                  <th>Submission</th>
                  <th>Original Decision</th>
                  <th>Override Decision</th>
                  <th>Override By</th>
                  <th>Reason</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {overrides.map((ov, idx) => (
                  <tr key={ov.OverrideID || ov.SK || idx}>
                    <td className="ld__table-insured">
                      {ov.SubmissionID || ov.InsuredName || '—'}
                    </td>
                    <td>
                      <span className="ld__status" data-status={ov.OriginalDecision}>
                        {ov.OriginalDecision || '—'}
                      </span>
                    </td>
                    <td>
                      <span className="ld__status" data-status={ov.OverrideDecision}>
                        {ov.OverrideDecision || '—'}
                      </span>
                    </td>
                    <td>{ov.OverrideBy || '—'}</td>
                    <td className="ld__override-reason">{ov.Reason || '—'}</td>
                    <td className="ld__table-date">{formatDate(ov.CreatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="ld__empty-section">No overrides recorded.</p>
          )}
        </div>
      </div>

      {/* Risk accumulation heat map */}
      <div className="ld__section">
        <h2 className="ld__section-title">Risk Accumulation</h2>
        <div className="ld__heatmap-wrap card">
          {typeof accumulationData === 'object' &&
          Object.keys(accumulationData).length > 0 ? (
            (() => {
              // Build heat map data
              const products = Object.entries(accumulationData);
              const allValues = products.flatMap(([, data]) => {
                if (typeof data === 'number') return [data];
                if (typeof data === 'object')
                  return Object.values(data).filter((v) => typeof v === 'number');
                return [];
              });
              const maxVal = Math.max(...allValues, 1);

              // If it's a nested structure (product -> region/category -> value)
              const isNested = products.some(
                ([, v]) => typeof v === 'object' && !Array.isArray(v)
              );

              if (isNested) {
                const allCategories = [
                  ...new Set(
                    products.flatMap(([, v]) =>
                      typeof v === 'object' ? Object.keys(v) : []
                    )
                  ),
                ];
                return (
                  <div className="ld__heatmap">
                    <table className="ld__heat-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          {allCategories.map((cat) => (
                            <th key={cat}>{cat}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {products.map(([product, data]) => (
                          <tr key={product}>
                            <td className="ld__heat-product">{product}</td>
                            {allCategories.map((cat) => {
                              const val = typeof data === 'object' ? data[cat] : 0;
                              return (
                                <td
                                  key={cat}
                                  className="ld__heat-cell"
                                  style={{
                                    backgroundColor: getHeatColor(val, maxVal),
                                    color: getHeatTextColor(val, maxVal),
                                  }}
                                >
                                  {val ?? 0}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              }

              // Simple product -> value structure
              return (
                <div className="ld__heatmap-simple">
                  {products.map(([product, value]) => (
                    <div
                      key={product}
                      className="ld__heat-block"
                      style={{
                        backgroundColor: getHeatColor(
                          typeof value === 'number' ? value : 0,
                          maxVal
                        ),
                        color: getHeatTextColor(
                          typeof value === 'number' ? value : 0,
                          maxVal
                        ),
                      }}
                    >
                      <span className="ld__heat-block-label">{product}</span>
                      <span className="ld__heat-block-value">
                        {typeof value === 'number' ? value : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()
          ) : (
            <p className="ld__empty-section">No risk accumulation data available.</p>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
